import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LeadPipelineCard from '../components/leads/LeadPipelineCard';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ChevronLeft, Mail, Phone, Building2, Edit3, Activity, TrendingUp,
    Clock, User, History, Briefcase, ExternalLink, X, Check,
    Linkedin, FileText, Zap, Users, Sparkles, Globe, Loader2, ChevronDown,
    GraduationCap, Award, Search, Calendar, MessageSquare, MessageCircle, Workflow, Camera, Database
} from 'lucide-react';
import { Button } from '../components/ui/button';
import EnrollCampaignModal from '../components/EnrollCampaignModal';
import ActivityCard from '../components/activities/ActivityCard';
import DISCBadge from '../components/DISCBadge';
import DISCEditor from '../components/DISCEditor';
import CustomFieldRenderer from '../components/CustomFieldRenderer';
import useCustomFields from '../hooks/useCustomFields';
import CampaignProgressTracker from '../components/CampaignProgressTracker';
import TagInput from '../components/TagInput';
import FollowUpPrompt from '../components/FollowUpPrompt';
import VoiceButton from '../components/ui/VoiceButton';
import MdFilePanel from '../components/MdFilePanel';
import PitchBookRecordTab from '../components/pitchbook/PitchBookRecordTab';
import { KEY_ROLE_LABELS } from '../components/table/CellRenderers';
import ContactEventPhotos from '../components/events/ContactEventPhotos';
import ContactEmailTypeahead from '../components/ContactEmailTypeahead';
import MeetingActionModal from '../components/MeetingActionModal';
import ContactEmailComposer from '../components/ContactEmailComposer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Canonical 8-value buying-role enum (matches backend BuyingRole). The
// legacy enum values ("Decision Maker", "User") are kept in the colour map
// so historic docs still render nicely while the server-side migration
// script backfills the new field.
const ROLES = [
    'Decision maker', 'Champion', 'Economic buyer', 'Influencer',
    'Technical buyer', 'End user', 'Blocker', 'Unknown',
];
const SENIORITY = ['C-suite', 'VP', 'Director', 'Manager', 'IC'];
const STRENGTHS = ['strong', 'moderate', 'weak', 'unknown'];
const ROLE_COLORS = {
    'Decision maker':  'bg-violet-100 text-violet-700',
    'Champion':        'bg-emerald-100 text-emerald-700',
    'Economic buyer':  'bg-violet-200 text-violet-800',
    'Influencer':      'bg-blue-100 text-blue-700',
    'Technical buyer': 'bg-cyan-100 text-cyan-700',
    'End user':        'bg-stone-100 text-stone-700',
    'Blocker':         'bg-red-100 text-red-700',
    'Unknown':         'bg-stone-100 text-stone-500',
    // Legacy labels for pre-migration contact docs:
    'Decision Maker':  'bg-violet-100 text-violet-700',
    'User':            'bg-stone-100 text-stone-700',
};
const STRENGTH_COLORS = { strong: 'text-emerald-600 bg-emerald-50', moderate: 'text-amber-600 bg-amber-50', weak: 'text-red-600 bg-red-50', unknown: 'text-stone-500 bg-stone-50' };
const FIELD_LABELS = {
    first_name: 'First Name', last_name: 'Last Name', email: 'Email', phone: 'Phone', mobile: 'Mobile',
    title: 'Job Title', department: 'Department', seniority: 'Seniority', linkedin_url: 'LinkedIn URL',
    role_in_deal: 'Role in Deal', buying_role: 'Buying Role',
    influence_score: 'Influence Score', engagement_score: 'Engagement Score',
    bio: 'Bio', tips_do: 'Coaching: Do', tips_dont: 'Coaching: Avoid',
    relationship_strength: 'Relationship', notes: 'Notes', tags: 'Tags', account_id: 'Company',
    owner_id: 'Owner',
};

const formatDate = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

function insightHasValue(value) {
    return value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
}

function formatInsightValue(value, { date = false } = {}) {
    if (!insightHasValue(value)) return '—';
    if (date) {
        const d = new Date(value);
        return isNaN(d) ? String(value) : d.toLocaleDateString();
    }
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function ContactInsightSection({ title, description, fields }) {
    const visible = fields.filter(f => insightHasValue(f.value));
    if (!visible.length) return null;
    return (
        <section className="overflow-hidden bg-white rounded-xl border border-stone-200">
            <div className="px-4 py-3 border-b border-stone-100 bg-stone-50/60">
                <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
                {description && <p className="text-xs text-stone-500 mt-0.5">{description}</p>}
            </div>
            <div className="grid grid-cols-1 gap-y-3 gap-x-8 p-4 md:grid-cols-2">
                {visible.map(f => (
                    <div key={f.label} className={f.full ? 'md:col-span-2' : ''}>
                        <div className="flex gap-3">
                            <span className="w-40 text-xs font-semibold tracking-wider uppercase text-stone-400 shrink-0">{f.label}</span>
                            {f.isLink ? (
                                <a href={String(f.value).startsWith('http') ? f.value : `https://${f.value}`} target="_blank" rel="noreferrer"
                                    className="text-sm text-blue-600 break-all hover:underline">
                                    {f.value}
                                </a>
                            ) : (
                                <span className="text-sm whitespace-pre-wrap break-words text-stone-700">{formatInsightValue(f.value, f)}</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ContactDataInsights({ contact }) {
    return (
        <div className="space-y-4">
            <div className="p-4 rounded-xl border bg-stone-50 border-stone-200">
                <div className="flex gap-3 items-start">
                    <Database className="w-5 h-5 text-stone-500 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Data Insights</h3>
                        <p className="mt-1 text-sm text-stone-600">Additional structured intelligence imported or enriched from trusted data sources. Relationship notes and coaching stay on About.</p>
                    </div>
                </div>
            </div>
            <ContactInsightSection title="Profile Details" fields={[
                { label: 'Prefix', value: contact.name_prefix },
                { label: 'Middle Name', value: contact.middle_name },
                { label: 'Gender', value: contact.gender },
                { label: 'Birth Year', value: contact.birth_year },
                { label: 'Fax', value: contact.fax },
            ]} />
            <ContactInsightSection title="Professional Context" fields={[
                { label: 'Primary Company', value: contact.primary_company_name },
                { label: 'Company Type', value: contact.primary_company_type },
                { label: 'Company Industry', value: contact.primary_company_industry },
                { label: 'Company Verticals', value: contact.primary_company_verticals },
                { label: 'Company Website', value: contact.primary_company_website, isLink: true },
                { label: 'Education', value: contact.education },
                { label: 'Biography', value: contact.bio, full: true },
            ]} />
            <ContactInsightSection title="Source Metadata" fields={[
                { label: 'Source', value: contact.source },
                { label: 'Source Tag', value: contact.source_tag },
                { label: 'Status Event', value: contact.status_event },
                { label: 'Created At', value: contact.created_at, date: true },
                { label: 'Updated At', value: contact.updated_at, date: true },
            ]} />
        </div>
    );
}

/* Reusable inline text field */
const InlineField = ({ label, value, field, editing, editForm, setEditForm, type }) => {
    if (!editing) return (
        <div>
            <p className="text-xs text-stone-400">{label}</p>
            <p className="text-sm font-medium break-words text-stone-800">{value || '--'}</p>
        </div>
    );
    if (type === 'select-seniority') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}</label>
            <select value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                <option value="">--</option>
                {SENIORITY.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>
    );
    if (type === 'select-role') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}</label>
            <select value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                <option value="">--</option>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
        </div>
    );
    if (type === 'select-strength') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}</label>
            <select value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                <option value="">--</option>
                {STRENGTHS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>
    );
    if (type === 'tags') {
        if (!editing) return (
            <div>
                <p className="text-xs text-stone-400">{label}</p>
                {Array.isArray(value) && value.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                        {value.map(t => (
                            <span key={t} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-stone-100 text-stone-600">{t}</span>
                        ))}
                    </div>
                ) : <p className="text-sm font-medium text-stone-800">--</p>}
            </div>
        );
        return (
            <div>
                <label className="block text-xs text-stone-400 mb-0.5">{label}</label>
                <TagInput
                    value={editForm[field] || []}
                    onChange={tags => setEditForm(p => ({ ...p, [field]: tags }))}
                    placeholder="Add tags..."
                />
            </div>
        );
    }
    if (type === 'number') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}</label>
            <input type="number" min="0" max="100" value={editForm[field] ?? ''} onChange={e => setEditForm(p => ({ ...p, [field]: parseInt(e.target.value) || 0 }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
        </div>
    );
    return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}</label>
            <input type={type || 'text'} value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                data-testid={`edit-${field}-input`}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
        </div>
    );
};

/**
 * CoachingCard — Bio + Do/Avoid tips. Surfaced by the Meeting Copilot
 * prep brief. Bio is a single text area; tips are per-line lists stored as
 * `tips_do: string[]` and `tips_dont: string[]`.
 */
function CoachingCard({ contact, editing, editForm, setEditForm }) {
    const doList = (editing ? editForm.tips_do : contact.tips_do) || [];
    const dontList = (editing ? editForm.tips_dont : contact.tips_dont) || [];

    const updateList = (field, value) => {
        const arr = value.split('\n').map(s => s.trim()).filter(Boolean);
        setEditForm(p => ({ ...p, [field]: arr }));
    };

    return (
        <div className="bg-white rounded-xl border border-stone-200">
            <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Coaching · Bio + Do/Avoid
                </h3>
                <span className="text-[10px] text-stone-400 uppercase tracking-widest font-bold">
                    Used by Meeting Copilot prep
                </span>
            </div>
            <div className="p-4 space-y-4 sm:p-5">
                <div>
                    <label className="block mb-1 text-xs text-stone-400">Bio</label>
                    {editing ? (
                        <textarea
                            value={editForm.bio || ''}
                            onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))}
                            rows={3}
                            placeholder="Short background + working style — 1-2 sentences the rep can scan before a call."
                            className="px-3 py-2 w-full text-sm rounded-md border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                        />
                    ) : (
                        <p className="p-3 text-sm whitespace-pre-wrap rounded-md border text-stone-700 bg-stone-50 border-stone-100">
                            {contact.bio || 'No bio yet. Click Edit and add a 1-2 sentence profile the rep can glance at before a call.'}
                        </p>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="block mb-1 text-xs font-bold tracking-widest text-emerald-600 uppercase">
                            ✓ Do
                        </label>
                        {editing ? (
                            <textarea
                                value={doList.join('\n')}
                                onChange={e => updateList('tips_do', e.target.value)}
                                rows={4}
                                placeholder={'One per line — e.g.\nOpen with the outcome, not the journey\nShow ROI in the first 5 minutes'}
                                className="px-3 py-2 w-full text-sm rounded-md border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-emerald-50/40"
                            />
                        ) : doList.length ? (
                            <ul className="p-3 space-y-1 rounded-md border border-emerald-100 bg-emerald-50/40">
                                {doList.map((t, i) => (
                                    <li key={i} className="flex gap-2 text-sm text-stone-800">
                                        <span className="flex-shrink-0 text-emerald-600">·</span>
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs italic text-stone-400">No Do tips yet.</p>
                        )}
                    </div>
                    <div>
                        <label className="block mb-1 text-xs font-bold tracking-widest text-rose-600 uppercase">
                            ✕ Avoid
                        </label>
                        {editing ? (
                            <textarea
                                value={dontList.join('\n')}
                                onChange={e => updateList('tips_dont', e.target.value)}
                                rows={4}
                                placeholder={'One per line — e.g.\nOverload with process detail\nRead from slides'}
                                className="px-3 py-2 w-full text-sm rounded-md border border-rose-200 focus:outline-none focus:ring-2 focus:ring-rose-500/20 bg-rose-50/40"
                            />
                        ) : dontList.length ? (
                            <ul className="p-3 space-y-1 rounded-md border border-rose-100 bg-rose-50/40">
                                {dontList.map((t, i) => (
                                    <li key={i} className="flex gap-2 text-sm text-stone-800">
                                        <span className="flex-shrink-0 text-rose-600">·</span>
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs italic text-stone-400">No Avoid tips yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function LinkedInDataSection({ data }) {
    const [expanded, setExpanded] = useState(true);
    if (!data) return null;

    const experiences = data.experiences || data.positions || [];
    const skills = data.skills || [];
    const education = data.education || [];
    const summary = data.summary || data.headline || '';

    return (
        <div className="bg-white rounded-xl border border-stone-200" data-testid="linkedin-data-section">
            <button
                onClick={() => setExpanded(!expanded)}
                className="flex justify-between items-center px-4 py-3 w-full border-b sm:px-5 border-stone-100"
            >
                <div className="flex gap-2 items-center">
                    <Linkedin className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>LinkedIn Profile</h3>
                </div>
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
            {expanded && (
                <div className="p-4 space-y-5 sm:p-5">
                    {/* Summary */}
                    {summary && (
                        <div>
                            <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-400">Summary</h4>
                            <p className="text-sm leading-relaxed text-stone-600">{summary}</p>
                        </div>
                    )}

                    {/* Experience */}
                    {experiences.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <Briefcase className="w-3.5 h-3.5" /> Experience
                            </h4>
                            <div className="space-y-3">
                                {experiences.map((exp, i) => (
                                    <div key={i} className="flex gap-3 pl-1">
                                        <div className="flex-shrink-0 mt-1 w-1 rounded-full bg-stone-200" style={{ minHeight: '2rem' }} />
                                        <div>
                                            <p className="text-sm font-medium text-stone-800">{exp.title || exp.role}</p>
                                            <p className="text-xs text-stone-500">{exp.company || exp.company_name}{exp.location ? ` - ${exp.location}` : ''}</p>
                                            {(exp.start_date || exp.starts_at || exp.date_range) && (
                                                <p className="text-xs text-stone-400 mt-0.5">
                                                    {exp.date_range || `${exp.start_date || exp.starts_at?.year || ''} - ${exp.end_date || exp.ends_at?.year || 'Present'}`}
                                                </p>
                                            )}
                                            {exp.description && <p className="mt-1 text-xs text-stone-500 line-clamp-2">{exp.description}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Skills */}
                    {skills.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <Award className="w-3.5 h-3.5" /> Skills
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                                {skills.slice(0, 20).map((skill, i) => (
                                    <span key={i} className="px-2 py-1 text-xs rounded-full border bg-stone-100 text-stone-700 border-stone-200">
                                        {typeof skill === 'string' ? skill : skill.name || skill.skill}
                                    </span>
                                ))}
                                {skills.length > 20 && (
                                    <span className="px-2 py-1 text-xs text-stone-400">+{skills.length - 20} more</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Education */}
                    {education.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <GraduationCap className="w-3.5 h-3.5" /> Education
                            </h4>
                            <div className="space-y-2">
                                {education.map((edu, i) => (
                                    <div key={i}>
                                        <p className="text-sm font-medium text-stone-800">{edu.school || edu.school_name || edu.institution}</p>
                                        <p className="text-xs text-stone-500">
                                            {edu.degree || edu.degree_name}{edu.field_of_study || edu.field ? `, ${edu.field_of_study || edu.field}` : ''}
                                        </p>
                                        {(edu.start_date || edu.starts_at || edu.date_range) && (
                                            <p className="text-xs text-stone-400">
                                                {edu.date_range || `${edu.start_date || edu.starts_at?.year || ''} - ${edu.end_date || edu.ends_at?.year || ''}`}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ContactDetail() {
    const { contactId } = useParams();
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.is_admin || user?.is_super_admin;
    const [contact, setContact] = useState(null);
    const [account, setAccount] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [activities, setActivities] = useState([]);
    const [colleagues, setColleagues] = useState([]);
    // Merged activity feed — filter pills slice the loaded timeline
    // client-side. `expandedActivityId` tracks which card is open inline
    // (HubSpot-style — no modal). Right-rail rows toggle the same state
    // and switch to the Activities tab, so a click anywhere deep-links
    // to the expanded card.
    const [activityFilter, setActivityFilter] = useState('all');
    const [expandedActivityId, setExpandedActivityId] = useState(null);
    const [deals, setDeals] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]); // Org users for Owner dropdown
    const [ownerSaving, setOwnerSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('about');
    // Unified edit state for both profile and key info
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [showRightSidebar, setShowRightSidebar] = useState(true);
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [showDISCEditor, setShowDISCEditor] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [showEnrichMenu, setShowEnrichMenu] = useState(false);
    const { fields: customFieldDefs } = useCustomFields('contact');

    // Quick log state
    const [quickLog, setQuickLog] = useState(null);
    const [logText, setLogText] = useState('');
    const [logCallDuration, setLogCallDuration] = useState('');
    const [logCallOutcome, setLogCallOutcome] = useState('completed');
    const [logTaskTitle, setLogTaskTitle] = useState('');
    const [logTaskDue, setLogTaskDue] = useState('');
    const [followUpPrompt, setFollowUpPrompt] = useState(null); // { type, title }
    const [aiTaskSuggestions, setAiTaskSuggestions] = useState(null); // null=hidden, []=loading done
    const [aiTaskLoading, setAiTaskLoading] = useState(false);

    // Communications tab
    const [timeline, setTimeline] = useState([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    // Meeting scheduler (legacy simple flow — kept while we migrate fully to MeetingActionModal)
    const [showMeetingScheduler, setShowMeetingScheduler] = useState(false);
    const [meetingForm, setMeetingForm] = useState({
        title: '', date: '', startTime: '09:00', endTime: '09:30',
        location: 'google_meet', notes: '', attendees: [],
    });
    // New unified Record/Schedule modal
    const [showMeetingModal, setShowMeetingModal] = useState(false);
    const [meetingModalTab, setMeetingModalTab] = useState('schedule');
    // Email composer
    const [showEmailComposer, setShowEmailComposer] = useState(false);

    const loadData = useCallback(async () => {
        try {
            const headers = getAuthHeader();
            const [cRes, accRes, dealsRes] = await Promise.all([
                axios.get(`${API}/contacts/${contactId}`, { headers }),
                axios.get(`${API}/accounts?page_size=500`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/deals`, { headers }).catch(() => ({ data: [] })),
            ]);
            setContact(cRes.data);
            const accData = Array.isArray(accRes.data) ? accRes.data : (accRes.data?.data || []);
            setAccounts(accData);
            const dealData = Array.isArray(dealsRes.data) ? dealsRes.data : (dealsRes.data?.data || []);
            setDeals(dealData.filter(d => d.account_id === cRes.data.account_id));
            if (cRes.data.account_id) {
                const [aRes, actRes, colRes] = await Promise.all([
                    axios.get(`${API}/accounts/${cRes.data.account_id}`, { headers }).catch(() => ({ data: null })),
                    axios.get(`${API}/activities?contact_id=${contactId}`, { headers }).catch(() => ({ data: [] })),
                    axios.get(`${API}/contacts?account_id=${cRes.data.account_id}&page_size=50`, { headers }).catch(() => ({ data: [] })),
                ]);
                setAccount(aRes.data);
                setActivities(actRes.data);
                const colData = Array.isArray(colRes.data) ? colRes.data : (colRes.data?.data || []);
                setColleagues(colData.filter(c => c.contact_id !== contactId));
            }
        } catch { toast.error('Contact not found'); navigate('/contacts'); }
        finally { setLoading(false); }
    }, [contactId]);

    useEffect(() => { loadData(); }, [loadData]);

    // Load org team members for Owner dropdown (one-time)
    useEffect(() => {
        axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() })
            .then(res => setTeamMembers(Array.isArray(res.data) ? res.data : []))
            .catch(() => setTeamMembers([]));
    }, [getAuthHeader]);

    // Reassign contact Owner (admin-only). Posts to /contacts/{id}/assign and
    // refreshes local state so the change_log tab shows the new entry.
    const handleReassignOwner = async (newOwnerId) => {
        if (!isAdmin) return;
        if (newOwnerId === (contact?.owner_id || '')) return;
        setOwnerSaving(true);
        try {
            const res = await axios.put(
                `${API}/contacts/${contactId}/assign`,
                { owner_id: newOwnerId || null },
                { headers: getAuthHeader() }
            );
            setContact(res.data);
            toast.success(newOwnerId ? 'Owner reassigned' : 'Owner cleared');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reassign owner');
        } finally {
            setOwnerSaving(false);
        }
    };

    const startEdit = () => { setEditForm({ ...contact }); setEditing(true); };
    const cancelEdit = () => { setEditing(false); setEditForm({}); };

    const handleSave = async () => {
        if (!editForm.first_name?.trim() || !editForm.last_name?.trim()) return toast.error('First and last name required');
        setSaving(true);
        try {
            const res = await axios.put(`${API}/contacts/${contactId}`, editForm, { headers: getAuthHeader() });
            setContact(res.data);
            setEditing(false);
            toast.success('Contact updated');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const enrichLinkedIn = async () => {
        if (!contact?.linkedin_url) return toast.error('No LinkedIn URL on this contact');
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const res = await axios.post(`${API}/enrichment/linkedin-profile`,
                { contact_id: contactId }, { headers: getAuthHeader() });
            if (res.data.enriched) {
                toast.success('LinkedIn profile enriched!');
                loadData();
            } else {
                toast.warning(res.data.message || 'No data returned');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrichment failed'); }
        finally { setEnriching(false); }
    };

    const enrichAI = async () => {
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            await axios.post(`${API}/enrichment/ai`,
                { record_type: 'contact', record_ids: [contactId] }, { headers: getAuthHeader() });
            toast.success('AI enrichment complete');
            loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrichment failed'); }
        finally { setEnriching(false); }
    };

    const enrichExa = async () => {
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const res = await axios.post(`${API}/enrichment/exa/contact`,
                { contact_id: contactId }, { headers: getAuthHeader() });
            if (res.data.enriched) {
                toast.success('Exa enrichment complete!');
                loadData();
            } else {
                toast.warning(res.data.message || 'No results found');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Exa enrichment failed'); }
        finally { setEnriching(false); }
    };

    // Load communications timeline
    const loadTimeline = useCallback(async () => {
        setLoadingTimeline(true);
        try {
            const res = await axios.get(`${API}/contacts/${contactId}/timeline`, { headers: getAuthHeader() });
            setTimeline(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch { /* timeline endpoint may not exist yet */ }
        finally { setLoadingTimeline(false); }
    }, [contactId, getAuthHeader]);

    useEffect(() => {
        if (contactId) loadTimeline();
    }, [contactId, loadTimeline]);

    // Save quick log
    const saveQuickLog = async () => {
        try {
            const headers = getAuthHeader();
            if (quickLog === 'note') {
                if (!logText.trim()) { toast.error('Note text required'); return; }
                // NB: backend Activity model stores the body in `description`,
                // not `notes` — the Activities / timeline readers all look at
                // `description`. Sending `notes` is a silent data-loss bug.
                await axios.post(`${API}/activities`, {
                    type: 'note', title: 'Note', description: logText,
                    contact_id: contactId, account_id: contact.account_id,
                    status: 'completed',
                }, { headers });
            } else if (quickLog === 'call') {
                await axios.post(`${API}/activities`, {
                    type: 'call', title: 'Phone Call', description: logText,
                    contact_id: contactId, account_id: contact.account_id,
                    status: 'completed',
                    duration: logCallDuration ? parseInt(logCallDuration) : undefined,
                    outcome: logCallOutcome,
                }, { headers });
            } else if (quickLog === 'task') {
                if (!logTaskTitle.trim()) { toast.error('Task title required'); return; }
                await axios.post(`${API}/activities`, {
                    type: 'task', title: logTaskTitle,
                    contact_id: contactId, account_id: contact.account_id,
                    status: 'planned',
                    due_date: logTaskDue || undefined,
                }, { headers });
            }
            const loggedType = quickLog;
            toast.success(`${quickLog} logged`);
            setQuickLog(null);
            setLogText('');
            setLogCallDuration('');
            setLogCallOutcome('completed');
            setLogTaskTitle('');
            setLogTaskDue('');
            loadData();
            loadTimeline();
            // Show follow-up prompt for note, call, meeting (not for task itself)
            if (['note', 'call'].includes(loggedType)) {
                setFollowUpPrompt({ type: loggedType });
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    };

    // AI Suggest Tasks
    const fetchAiTaskSuggestions = async () => {
        setAiTaskLoading(true);
        setAiTaskSuggestions([]);
        try {
            const res = await axios.post(`${API}/ai/suggest-tasks`, {
                context_type: 'contact',
                context_id: contactId,
            }, { headers: getAuthHeader() });
            setAiTaskSuggestions(res.data.suggestions || []);
            if ((res.data.suggestions || []).length === 0) toast.info('No suggestions right now');
        } catch (err) {
            toast.error('Failed to get AI suggestions');
            setAiTaskSuggestions(null);
        } finally {
            setAiTaskLoading(false);
        }
    };

    const acceptAiTask = async (suggestion, idx) => {
        try {
            await axios.post(`${API}/activities`, {
                type: 'task',
                title: suggestion.title,
                description: suggestion.description,
                priority: suggestion.priority || 'medium',
                due_date: suggestion.due_date || undefined,
                contact_id: contactId,
                account_id: contact?.account_id,
                status: 'planned',
                source: 'ai_suggested',
            }, { headers: getAuthHeader() });
            toast.success('Task created');
            setAiTaskSuggestions(prev => prev.filter((_, i) => i !== idx));
        } catch {
            toast.error('Failed to create task');
        }
    };

    // Schedule meeting
    const handleScheduleMeeting = async () => {
        if (!meetingForm.title.trim() || !meetingForm.date) {
            toast.error('Title and date are required');
            return;
        }
        try {
            const startDateTime = `${meetingForm.date}T${meetingForm.startTime}:00`;
            const endDateTime = `${meetingForm.date}T${meetingForm.endTime}:00`;
            const attendeeEmails = [
                ...(contact.email ? [contact.email] : []),
                ...meetingForm.attendees.map(a => a.email),
            ];
            await axios.post(`${API}/oauth/calendar/events`, {
                summary: meetingForm.title,
                description: meetingForm.notes,
                start_datetime: startDateTime,
                end_datetime: endDateTime,
                attendees: attendeeEmails,
                location: meetingForm.location === 'google_meet' ? 'Google Meet' :
                          meetingForm.location === 'zoom' ? 'Zoom' : meetingForm.location,
                add_meet: meetingForm.location === 'google_meet',
            }, { headers: getAuthHeader() });
            // Also log as activity
            await axios.post(`${API}/activities`, {
                type: 'meeting', title: meetingForm.title, notes: meetingForm.notes,
                contact_id: contactId, account_id: contact.account_id,
                status: 'scheduled',
            }, { headers: getAuthHeader() });
            toast.success('Meeting scheduled');
            setShowMeetingScheduler(false);
            setMeetingForm({ title: '', date: '', startTime: '09:00', endTime: '09:30', location: 'google_meet', notes: '', attendees: [] });
            loadData();
            loadTimeline();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to schedule meeting'); }
    };

    if (loading) return <div className="flex justify-center items-center h-64"><div className="w-8 h-8 rounded-full border-4 animate-spin border-stone-700 border-t-transparent" /></div>;
    if (!contact) return null;

    const changeLog = [...(contact.change_log || [])].reverse();
    const accountName = accounts.find(a => a.account_id === contact.account_id)?.name || '--';

    const sharedProps = { editing, editForm, setEditForm };

    return (
        <div className="-m-6 animate-fade-in">
            {/* Top bar */}
            <div className="flex justify-between items-center px-4 py-3 bg-white border-b sm:px-6 border-stone-200">
                <button onClick={() => navigate('/contacts')} className="flex gap-1 items-center text-sm text-stone-500 hover:text-stone-700" data-testid="back-to-contacts">
                    <ChevronLeft className="w-4 h-4" />Contacts
                </button>
                {/* Edit/Save controls in top bar for mobile/tablet */}
                <div className="flex gap-2 items-center">
                    {!editing && (
                        <div className="relative">
                            <Button variant="outline" size="sm" onClick={() => setShowEnrichMenu(!showEnrichMenu)} disabled={enriching} className="h-8">
                                {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                                Enrich
                            </Button>
                            {showEnrichMenu && (
                                <div className="absolute right-0 top-full z-10 py-1 mt-1 w-48 bg-white rounded-lg border shadow-lg border-stone-200">
                                    <button onClick={enrichAI} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                        <Sparkles className="w-3.5 h-3.5 text-amber-500" /> AI Enrich
                                    </button>
                                    <button onClick={enrichLinkedIn} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                        <Globe className="w-3.5 h-3.5 text-blue-500" /> LinkedIn Profile
                                    </button>
                                    <button onClick={enrichExa} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                        <Search className="w-3.5 h-3.5 text-indigo-500" /> Exa Search
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {!editing && (
                        <Button size="sm" variant="outline" onClick={() => setShowEnrollModal(true)} className="h-8" data-testid="enroll-campaign-btn">
                            <Workflow className="w-3.5 h-3.5 mr-1.5" />Enroll in Campaign
                        </Button>
                    )}
                    {!editing ? (
                        <Button size="sm" variant="outline" onClick={startEdit} className="h-8" data-testid="inline-edit-btn">
                            <Edit3 className="w-3.5 h-3.5 mr-1.5" />Edit
                        </Button>
                    ) : (
                        <>
                            <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8 text-stone-500" data-testid="cancel-edit-btn">
                                <X className="w-3.5 h-3.5 mr-1" />Cancel
                            </Button>
                            <Button size="sm" onClick={handleSave} disabled={saving} className="h-8 bg-stone-900 hover:bg-stone-800" data-testid="save-inline-btn">
                                <Check className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : 'Save'}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Main layout - responsive */}
            <div className="flex flex-col xl:flex-row min-h-[calc(100vh-120px)]">

                {/* ─── LEFT SIDEBAR ─── */}
                <div className="overflow-y-auto w-full bg-white border-b xl:w-64 xl:flex-shrink-0 xl:border-b-0 xl:border-r border-stone-200">
                    {/* Contact Header */}
                    <div className="p-4 border-b sm:p-5 border-stone-100">
                        <div className="flex gap-4 items-center lg:flex-col lg:items-center lg:gap-0">
                            <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-16 h-16 bg-gradient-to-br rounded-full lg:w-20 lg:h-20 from-stone-100 to-stone-200 lg:mb-3" data-testid="contact-avatar">
                                {contact.avatar_url ? (
                                    <img src={contact.avatar_url} alt={`${contact.first_name} ${contact.last_name}`} className="object-cover w-full h-full" />
                                ) : (
                                    <span className="text-xl font-bold lg:text-2xl text-stone-700">{contact.first_name?.charAt(0)}{contact.last_name?.charAt(0)}</span>
                                )}
                            </div>
                            <div className="flex-1 text-left lg:text-center lg:flex-none">
                                <h1 className="text-base font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="contact-detail-name">
                                    {contact.first_name} {contact.last_name}
                                </h1>
                                <p className="text-xs text-stone-500 mt-0.5">{contact.title || '--'}</p>
                                {contact.key_role && (
                                    <span className="inline-flex px-2 py-0.5 mt-1 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700">
                                        {KEY_ROLE_LABELS[contact.key_role] || contact.key_role.replace(/_/g, ' ')}
                                    </span>
                                )}
                                {account && (
                                    <button onClick={() => navigate(`/accounts/${account.account_id}`)} className="flex gap-1 items-center mt-1 text-xs text-stone-700 hover:text-stone-900 lg:mx-auto">
                                        <Building2 className="w-3 h-3" />{account.name}
                                    </button>
                                )}
                                {contact.email && (
                                    <a href={`mailto:${contact.email}`} className="flex gap-1 items-center mt-1 text-xs break-all text-stone-700 hover:underline lg:justify-center" data-testid="contact-email-link">
                                        {contact.email} <ExternalLink className="flex-shrink-0 w-3 h-3" />
                                    </a>
                                )}
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap lg:justify-center">
                                    {contact.disc_profile && <DISCBadge profile={contact.disc_profile} secondary={contact.disc_secondary} size="md" />}
                                    {(contact.buying_role || contact.role_in_deal) && <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[(contact.buying_role || contact.role_in_deal)] || 'bg-stone-100 text-stone-600'}`}>{(contact.buying_role || contact.role_in_deal)}</span>}
                                    {contact.relationship_strength && <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STRENGTH_COLORS[contact.relationship_strength] || ''}`}>{contact.relationship_strength}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Colleagues — other contacts at the same company */}
                    {colleagues.length > 0 && (
                        <div className="p-4 border-b border-stone-100">
                            <div className="flex justify-between items-center mb-2">
                                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Users className="w-3 h-3" /> Colleagues
                                    <span className="font-semibold text-stone-400">{colleagues.length}</span>
                                </p>
                                {account && (
                                    <button
                                        onClick={() => navigate(`/companies/${account.account_id}?tab=org-chart`)}
                                        className="text-[10px] text-stone-500 hover:text-stone-700"
                                        title="View org chart"
                                    >
                                        Org Chart →
                                    </button>
                                )}
                            </div>
                            <div className="space-y-1">
                                {colleagues.slice(0, 6).map(c => (
                                    <button
                                        key={c.contact_id}
                                        onClick={() => navigate(`/contacts/${c.contact_id}`)}
                                        className="w-full flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-stone-50 text-left transition-colors"
                                    >
                                        <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-7 h-7 bg-gradient-to-br rounded-full from-stone-100 to-stone-200">
                                            {c.avatar_url ? (
                                                <img src={c.avatar_url} alt="" className="object-cover w-full h-full" />
                                            ) : (
                                                <span className="text-[10px] font-semibold text-stone-700">
                                                    {(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold truncate text-stone-800">
                                                {c.first_name} {c.last_name}
                                            </p>
                                            {c.title && (
                                                <p className="text-[10px] text-stone-500 truncate">{c.title}</p>
                                            )}
                                        </div>
                                        {c.disc_profile && (
                                            <DISCBadge profile={c.disc_profile} secondary={c.disc_secondary} size="sm" />
                                        )}
                                    </button>
                                ))}
                                {colleagues.length > 6 && account && (
                                    <button
                                        onClick={() => navigate(`/companies/${account.account_id}?tab=contacts`)}
                                        className="w-full text-[10px] text-stone-500 hover:text-stone-700 text-center py-1"
                                    >
                                        + {colleagues.length - 6} more
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Key Information - Editable */}
                    <div className="hidden p-4 xl:block">
                        <p className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Key Information</p>
                        <div className="space-y-3">
                            <InlineField label="Email" value={contact.email} field="email" type="email" {...sharedProps} />
                            <InlineField label="Phone" value={contact.phone} field="phone" {...sharedProps} />
                            <InlineField label="Mobile" value={contact.mobile} field="mobile" {...sharedProps} />
                            <InlineField label="Department" value={contact.department} field="department" {...sharedProps} />
                            <InlineField label="Seniority" value={contact.seniority} field="seniority" type="select-seniority" {...sharedProps} />
                            {!editing ? (
                                <div>
                                    <p className="text-xs text-stone-400">Company</p>
                                    <p className="text-sm font-medium text-stone-800">{accountName}</p>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs text-stone-400 mb-0.5">Company</label>
                                    <select value={editForm.account_id || ''} onChange={e => setEditForm(p => ({ ...p, account_id: e.target.value }))}
                                        data-testid="edit-account-select"
                                        className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                        <option value="">--</option>
                                        {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                                    </select>
                                </div>
                            )}
                            <InlineField label="Influence Score" value={contact.influence_score != null ? `${contact.influence_score}/100` : null} field="influence_score" type="number" {...sharedProps} />
                            <InlineField label="Engagement Score" value={contact.engagement_score != null ? `${contact.engagement_score}/100` : null} field="engagement_score" type="number" {...sharedProps} />
                            <InlineField label="Relationship" value={contact.relationship_strength} field="relationship_strength" type="select-strength" {...sharedProps} />
                            <InlineField label="Role in Deal" value={(contact.buying_role || contact.role_in_deal)} field="buying_role" type="select-role" {...sharedProps} />
                            <InlineField label="Source Tag" value={contact.source_tag} field="source_tag" {...sharedProps} />
                            <InlineField label="Status Event" value={contact.status_event} field="status_event" {...sharedProps} />
                            <div className="col-span-2">
                                <InlineField label="Tags" value={contact.tags} field="tags" type="tags" {...sharedProps} />
                            </div>
                            {!editing && (
                                <>
                                    <div>
                                        <p className="text-xs text-stone-400">Owner</p>
                                        {isAdmin ? (
                                            <select
                                                data-testid="contact-owner-select"
                                                value={contact.owner_id || ''}
                                                disabled={ownerSaving}
                                                onChange={e => handleReassignOwner(e.target.value)}
                                                className="mt-0.5 w-full text-sm text-stone-800 font-medium bg-transparent border border-transparent hover:border-stone-200 focus:border-stone-400 focus:outline-none rounded px-1 py-0.5 -ml-1 cursor-pointer">
                                                <option value="">(unassigned)</option>
                                                {teamMembers.map(m => (
                                                    <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <p className="text-sm font-medium text-stone-800">{contact.owner_name || '--'}</p>
                                        )}
                                    </div>
                                    <div><p className="text-xs text-stone-400">Last Updated</p><p className="text-sm font-medium text-stone-800">{formatDate(contact.updated_at)}</p></div>
                                    <div><p className="text-xs text-stone-400">Created</p><p className="text-sm font-medium text-stone-800">{formatDate(contact.created_at)}</p></div>
                                </>
                            )}
                        </div>
                        {contact.linkedin_url && !editing && (
                            <a href={contact.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-stone-700 hover:underline mt-3" data-testid="linkedin-link">
                                <Linkedin className="w-3.5 h-3.5" />LinkedIn Profile
                            </a>
                        )}
                        {editing && (
                            <div className="mt-3">
                                <InlineField label="LinkedIn URL" value={contact.linkedin_url} field="linkedin_url" {...sharedProps} />
                            </div>
                        )}
                    </div>
                </div>

                {/* ─── CENTER COLUMN ─── */}
                <div className="overflow-y-auto flex-1 min-w-0 bg-stone-50">
                    {/* Quick Log Buttons */}
                    <div className="px-4 pt-3 pb-0 bg-white sm:px-6">
                        <div className="flex gap-2 items-center mb-0">
                            <button onClick={() => setQuickLog(quickLog === 'note' ? null : 'note')}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${quickLog === 'note' ? 'bg-stone-900 text-white' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`}>
                                <FileText className="w-3 h-3" /> Note
                            </button>
                            <button onClick={() => setQuickLog(quickLog === 'call' ? null : 'call')}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${quickLog === 'call' ? 'bg-stone-900 text-white' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`}>
                                <Phone className="w-3 h-3" /> Call
                            </button>
                            <button onClick={() => setQuickLog(quickLog === 'task' ? null : 'task')}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${quickLog === 'task' ? 'bg-stone-900 text-white' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`}>
                                <Check className="w-3 h-3" /> Task
                            </button>
                            <button onClick={() => { setMeetingModalTab('record'); setShowMeetingModal(true); }}
                                className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center gap-1 transition-colors"
                                data-testid="record-meeting-button"
                                title="Log a meeting that already happened">
                                <Calendar className="w-3 h-3" /> Record
                            </button>
                            <button onClick={() => { setMeetingModalTab('schedule'); setShowMeetingModal(true); }}
                                className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center gap-1 transition-colors"
                                data-testid="schedule-meeting-button"
                                title="Schedule a new meeting with invite">
                                <Calendar className="w-3 h-3" /> Schedule
                            </button>
                            <button onClick={() => setShowEmailComposer(true)}
                                className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center gap-1 transition-colors"
                                data-testid="compose-email-button"
                                title="Compose an email with templates, signatures, and AI writer">
                                <Mail className="w-3 h-3" /> Email
                            </button>
                            <div className="mx-1 w-px h-5 bg-stone-200" />
                            <button
                                onClick={() => aiTaskSuggestions !== null ? setAiTaskSuggestions(null) : fetchAiTaskSuggestions()}
                                disabled={aiTaskLoading}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${
                                    aiTaskSuggestions !== null
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200'
                                }`}
                            >
                                {aiTaskLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                AI Tasks
                            </button>
                            {/* Event Outreach — AI-drafts a 3-message × multi-channel
                                sequence tied to a specific event the contact is
                                registered for or attended. */}
                            <button
                                onClick={() => navigate(`/contacts/${contactId}/event-outreach`)}
                                data-action="contact.event_outreach"
                                title="AI Event Outreach Studio"
                                className="text-xs pl-2 pr-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200"
                            >
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-violet-600 text-white rounded-full px-1.5 py-0.5">
                                    <Sparkles className="w-2.5 h-2.5" /> AI
                                </span>
                                <span className="font-semibold">Event Outreach</span>
                            </button>
                        </div>
                    </div>

                    {/* Quick Log Form */}
                    {quickLog && (
                        <div className="p-3 mx-4 mt-3 rounded-lg border sm:mx-6 bg-stone-50 border-stone-200">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-semibold capitalize text-stone-700">Log {quickLog}</span>
                                <button onClick={() => setQuickLog(null)} className="text-stone-400 hover:text-stone-600">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            {quickLog === 'note' && (
                                <div className="flex gap-1.5">
                                    <textarea value={logText} onChange={e => setLogText(e.target.value)} rows={2}
                                        placeholder="Write a note..."
                                        className="flex-1 text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    <VoiceButton onTranscript={t => setLogText(p => p ? `${p} ${t}` : t)} />
                                </div>
                            )}
                            {quickLog === 'call' && (
                                <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] text-stone-500 mb-0.5">Duration (min)</label>
                                            <input type="number" value={logCallDuration} onChange={e => setLogCallDuration(e.target.value)}
                                                placeholder="0" className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] text-stone-500 mb-0.5">Outcome</label>
                                            <select value={logCallOutcome} onChange={e => setLogCallOutcome(e.target.value)}
                                                className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                                <option value="completed">Completed</option>
                                                <option value="no_answer">No Answer</option>
                                                <option value="voicemail">Voicemail</option>
                                                <option value="busy">Busy</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <textarea value={logText} onChange={e => setLogText(e.target.value)} rows={2}
                                            placeholder="Call notes..."
                                            className="flex-1 text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        <VoiceButton onTranscript={t => setLogText(p => p ? `${p} ${t}` : t)} />
                                    </div>
                                </div>
                            )}
                            {quickLog === 'task' && (
                                <div className="space-y-2">
                                    <input value={logTaskTitle} onChange={e => setLogTaskTitle(e.target.value)}
                                        placeholder="Task title..."
                                        className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    <input type="date" value={logTaskDue} onChange={e => setLogTaskDue(e.target.value)}
                                        className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                </div>
                            )}
                            <button onClick={saveQuickLog}
                                className="px-3 py-1 mt-2 text-xs text-white rounded-lg transition-colors bg-stone-900 hover:bg-stone-800">
                                Save
                            </button>
                        </div>
                    )}

                    {/* AI Task Suggestions */}
                    {aiTaskSuggestions !== null && (
                        <div className="p-3 mx-4 mt-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 sm:mx-6">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                                    <Sparkles className="w-3.5 h-3.5" /> AI Task Suggestions
                                </span>
                                <button onClick={() => setAiTaskSuggestions(null)} className="text-stone-400 hover:text-stone-600">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            {aiTaskLoading ? (
                                <div className="flex justify-center items-center py-4">
                                    <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                                    <span className="ml-2 text-xs text-stone-500">Analyzing contact...</span>
                                </div>
                            ) : aiTaskSuggestions.length === 0 ? (
                                <p className="py-2 text-xs text-center text-stone-500">No suggestions. All caught up!</p>
                            ) : (
                                <div className="space-y-2">
                                    {aiTaskSuggestions.map((s, idx) => (
                                        <div key={idx} className="bg-white rounded-lg border border-stone-200 p-2.5 flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <span className="text-xs font-medium text-stone-900">{s.title}</span>
                                                {s.reasoning && (
                                                    <p className="text-[11px] text-stone-500 mt-0.5">{s.reasoning}</p>
                                                )}
                                                <div className="flex items-center gap-2 mt-1 text-[10px] text-stone-400">
                                                    {s.due_date && <span>{new Date(s.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                                                    {s.priority && <span className="capitalize">{s.priority}</span>}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => acceptAiTask(s, idx)}
                                                className="px-2 py-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 shrink-0"
                                            >
                                                Accept
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="overflow-x-auto px-4 mt-0 bg-white border-b border-stone-200 sm:px-6">
                        <div className="flex gap-0 items-center min-w-max">
                            {[
                                { key: 'about', label: 'About', icon: User },
                                { key: 'data_insights', label: 'Data Insights', icon: Database },
                                // Merged single feed with filter pills — Communications
                                // tab was redundant (both rendered the activities
                                // collection via different endpoints).
                                { key: 'activities', label: 'Activities', icon: Activity },
                                { key: 'event_photos', label: 'Event Photos', icon: Camera },
                                { key: 'changelog', label: 'Change Log', icon: History },
                                { key: 'ai_context', label: 'AI Context', icon: Sparkles },
                                ...(contact?.has_pitchbook_data
                                    ? [{ key: 'pitchbook', label: 'PitchBook', icon: Database }] : []),
                            ].map(tab => (
                                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                    className={`flex items-center gap-1.5 px-4 sm:px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.key ? 'border-stone-700 text-stone-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
                                    data-testid={`tab-${tab.key}`}>
                                    <tab.icon className="w-4 h-4" />{tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 sm:p-6">
                        {/* ─── ABOUT TAB ─── */}
                        {activeTab === 'about' && (
                            <div className="space-y-6">
                                {/* Contact Profile */}
                                <div className="bg-white rounded-xl border border-stone-200" data-testid="contact-profile-section">
                                    <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                                        <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Profile</h3>
                                    </div>
                                    <div className="p-4 sm:p-5">
                                        <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2 xl:grid-cols-3">
                                            <InlineField label="First Name" value={contact.first_name} field="first_name" {...sharedProps} />
                                            <InlineField label="Last Name" value={contact.last_name} field="last_name" {...sharedProps} />
                                            <InlineField label="Email" value={contact.email} field="email" type="email" {...sharedProps} />
                                            <InlineField label="Phone" value={contact.phone} field="phone" {...sharedProps} />
                                            <InlineField label="Mobile" value={contact.mobile} field="mobile" {...sharedProps} />
                                            <InlineField label="LinkedIn URL" value={contact.linkedin_url} field="linkedin_url" {...sharedProps} />
                                            <InlineField label="Job Title" value={contact.title} field="title" {...sharedProps} />
                                            <InlineField label="Department" value={contact.department} field="department" {...sharedProps} />
                                            {!editing ? (
                                                <div><p className="text-xs text-stone-400">Company</p><p className="text-sm font-medium text-stone-800">{accountName}</p></div>
                                            ) : (
                                                <div>
                                                    <label className="block text-xs text-stone-400 mb-0.5">Company</label>
                                                    <select value={editForm.account_id || ''} onChange={e => setEditForm(p => ({ ...p, account_id: e.target.value }))}
                                                        className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                                        <option value="">--</option>
                                                        {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                                                    </select>
                                                </div>
                                            )}
                                            <InlineField label="Seniority" value={contact.seniority} field="seniority" type="select-seniority" {...sharedProps} />
                                            <InlineField label="Role in Deal" value={(contact.buying_role || contact.role_in_deal)} field="buying_role" type="select-role" {...sharedProps} />
                                            <InlineField label="Relationship" value={contact.relationship_strength} field="relationship_strength" type="select-strength" {...sharedProps} />
                                        </div>
                                    </div>
                                    {/* Notes */}
                                    <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                                        {!editing ? (
                                            contact.notes ? (
                                                <div>
                                                    <p className="mb-1 text-xs text-stone-400">Notes</p>
                                                    <p className="p-3 text-sm whitespace-pre-wrap rounded-md border text-stone-700 bg-stone-50 border-stone-100">{contact.notes}</p>
                                                </div>
                                            ) : null
                                        ) : (
                                            <div>
                                                <label className="block text-xs text-stone-400 mb-0.5">Notes</label>
                                                <textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={3}
                                                    data-testid="edit-notes-input"
                                                    className="w-full px-2.5 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20 bg-white" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Coaching — Bio + Do/Avoid tips used by the Meeting Copilot prep brief */}
                                <CoachingCard contact={contact} editing={editing} editForm={editForm} setEditForm={setEditForm} />

                                {/* Lead-level sales pipeline + funnel fields (VSL funnel, Batch 1) */}
                                <LeadPipelineCard
                                    contact={contact}
                                    contactId={contactId}
                                    teamMembers={teamMembers}
                                    getAuthHeader={getAuthHeader}
                                    onChange={loadData}
                                />

                                {/* DISC Personality Profile */}
                                <div className="bg-white rounded-xl border border-stone-200" data-testid="disc-profile-section">
                                    <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                                        <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>DISC Personality Profile</h3>
                                        <Button variant="outline" size="sm" onClick={() => setShowDISCEditor(true)}>
                                            {contact.disc_profile ? 'Edit DISC' : 'Assess DISC'}
                                        </Button>
                                    </div>
                                    <div className="p-4 sm:p-5">
                                        {contact.disc_profile ? (
                                            <div className="space-y-4">
                                                <DISCBadge profile={contact.disc_profile} secondary={contact.disc_secondary} scores={contact.disc_scores} size="lg" />
                                                {contact.communication_preferences?.email_tips && (
                                                    <div>
                                                        <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-500">Communication Tips</h4>
                                                        <ul className="space-y-1">
                                                            {contact.communication_preferences.email_tips.map((tip, i) => (
                                                                <li key={i} className="flex gap-2 items-start text-sm text-stone-600">
                                                                    <span className="text-emerald-500 mt-0.5">+</span> {tip}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {contact.communication_preferences?.avoid && (
                                                    <div>
                                                        <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-500">Avoid</h4>
                                                        <ul className="space-y-1">
                                                            {contact.communication_preferences.avoid.map((tip, i) => (
                                                                <li key={i} className="flex gap-2 items-start text-sm text-stone-500">
                                                                    <span className="text-red-400 mt-0.5">-</span> {tip}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {contact.disc_notes && (
                                                    <div>
                                                        <h4 className="mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Notes</h4>
                                                        <p className="text-sm text-stone-600">{contact.disc_notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="py-6 text-center">
                                                <p className="text-sm text-stone-400">No DISC profile set.</p>
                                                <p className="mt-1 text-xs text-stone-400">Click "Assess DISC" to analyze communication style with AI or set manually.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Assessment Scores (Affiliate Products) */}
                                {contact.assessments?.aiagilitycheck && (() => {
                                    const aac = contact.assessments.aiagilitycheck;
                                    const scores = aac.scores || {};
                                    const pillars = [
                                        { key: 'strategy', label: 'Strategy', color: '#6366f1' },
                                        { key: 'people', label: 'People', color: '#f59e0b' },
                                        { key: 'technology', label: 'Technology', color: '#10b981' },
                                        { key: 'execution', label: 'Execution', color: '#ef4444' },
                                    ];
                                    const total = aac.total_score || 0;
                                    const maxScore = 240;
                                    const pct = Math.round((total / maxScore) * 100);
                                    const level = pct >= 75 ? 'Advanced' : pct >= 50 ? 'Developing' : pct >= 25 ? 'Emerging' : 'Beginning';
                                    const levelColor = pct >= 75 ? 'text-emerald-600 bg-emerald-50' : pct >= 50 ? 'text-blue-600 bg-blue-50' : pct >= 25 ? 'text-amber-600 bg-amber-50' : 'text-stone-600 bg-stone-100';
                                    return (
                                        <div className="bg-white rounded-xl border border-stone-200" data-testid="assessment-scores-section">
                                            <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                                                <div className="flex gap-2 items-center">
                                                    <div className="flex justify-center items-center w-5 h-5 bg-indigo-600 rounded">
                                                        <span className="text-white text-[10px] font-bold">AI</span>
                                                    </div>
                                                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Agility Check</h3>
                                                </div>
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${levelColor}`}>{level}</span>
                                            </div>
                                            <div className="p-4 sm:p-5">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div>
                                                        <p className="text-2xl font-bold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{total}<span className="text-sm font-normal text-stone-400">/{maxScore}</span></p>
                                                        <p className="text-xs text-stone-400 mt-0.5">Overall Score</p>
                                                    </div>
                                                    <div className="text-xs text-right text-stone-400">
                                                        {aac.completed_at && <p>Completed {new Date(aac.completed_at).toLocaleDateString()}</p>}
                                                        {aac.registered_at && !aac.completed_at && <p>Registered {new Date(aac.registered_at).toLocaleDateString()}</p>}
                                                        {aac.status && <p className="capitalize mt-0.5">{aac.status}</p>}
                                                    </div>
                                                </div>
                                                <div className="space-y-3">
                                                    {pillars.map(p => {
                                                        const val = scores[p.key] || 0;
                                                        const pillarPct = Math.round((val / 60) * 100);
                                                        return (
                                                            <div key={p.key}>
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="text-xs font-medium text-stone-600">{p.label}</span>
                                                                    <span className="text-xs text-stone-400">{val}/60</span>
                                                                </div>
                                                                <div className="overflow-hidden w-full h-2 rounded-full bg-stone-100">
                                                                    <div className="h-full rounded-full transition-all" style={{ width: `${pillarPct}%`, backgroundColor: p.color }} />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Custom Fields */}
                                {customFieldDefs.length > 0 && (
                                    <div className="bg-white rounded-xl border border-stone-200" data-testid="custom-fields-section">
                                        <div className="px-4 py-3 border-b sm:px-5 border-stone-100">
                                            <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Custom Fields</h3>
                                        </div>
                                        <div className="p-4 sm:p-5">
                                            <CustomFieldRenderer
                                                fields={customFieldDefs}
                                                values={editing ? (editForm.custom_fields || contact?.custom_fields || {}) : (contact?.custom_fields || {})}
                                                editing={editing}
                                                onChange={(newCustomFields) => setEditForm(prev => ({ ...prev, custom_fields: newCustomFields }))}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Campaign Journey Progress */}
                                <CampaignProgressTracker contactId={contact?.contact_id} />

                                {/* LinkedIn Data */}
                                {contact.linkedin_data && (
                                    <LinkedInDataSection data={contact.linkedin_data} />
                                )}
                            </div>
                        )}

                        {/* ─── DATA INSIGHTS TAB ─── */}
                        {activeTab === 'data_insights' && (
                            <ContactDataInsights contact={contact} />
                        )}

                        {/* ─── ACTIVITIES TAB (merged feed) ─── */}
                        {activeTab === 'activities' && (
                            <div className="p-4 bg-white rounded-xl border border-stone-200 sm:p-5">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Activities</h3>
                                </div>
                                {/* Filter pills. Source: the already-merged /timeline
                                    endpoint, so adding a new activity type upstream
                                    surfaces here with no client changes. */}
                                <div className="flex items-center gap-1.5 flex-wrap mb-4 overflow-x-auto">
                                    {[
                                        { key: 'all', label: 'All activities' },
                                        { key: 'note', label: 'Notes' },
                                        { key: 'email', label: 'Emails' },
                                        { key: 'call', label: 'Calls' },
                                        { key: 'task', label: 'Tasks' },
                                        { key: 'meeting', label: 'Meetings' },
                                        // Event Outreach Studio drops task_kind='outreach_send'
                                        // on its generated send-tasks — this pill filters to them.
                                        { key: 'outreach', label: 'Outreach' },
                                    ].map(pill => {
                                        const active = activityFilter === pill.key;
                                        return (
                                            <button
                                                key={pill.key}
                                                onClick={() => setActivityFilter(pill.key)}
                                                data-action="contact.filter_activities"
                                                className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                                                    active
                                                        ? 'text-white bg-stone-900'
                                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                                }`}
                                            >
                                                {pill.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {(() => {
                                    const feed = activityFilter === 'all'
                                        ? timeline
                                        : activityFilter === 'outreach'
                                            ? timeline.filter(i => i.task_kind === 'outreach_send')
                                            : timeline.filter(i => (i.type || '').toLowerCase() === activityFilter);
                                    if (loadingTimeline) {
                                        return (
                                            <div className="py-10 text-center">
                                                <Loader2 className="mx-auto w-6 h-6 animate-spin text-stone-300" />
                                            </div>
                                        );
                                    }
                                    if (feed.length === 0) {
                                        return (
                                            <div className="py-10 text-center text-stone-400">
                                                <Activity className="mx-auto mb-2 w-10 h-10 opacity-40" />
                                                <p className="text-sm">
                                                    {activityFilter === 'all'
                                                        ? 'No activities yet'
                                                        : `No ${activityFilter}s yet`}
                                                </p>
                                                <p className="mt-1 text-xs">
                                                    Log a note, call, task, or meeting above to start building the timeline.
                                                </p>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="space-y-2">
                                            {feed.map((item, idx) => {
                                                const key = item.activity_id || item.id || idx;
                                                const isExpanded = expandedActivityId === key;
                                                return (
                                                    <ActivityCard
                                                        key={key}
                                                        activity={item}
                                                        expanded={isExpanded}
                                                        autoFocus={isExpanded}
                                                        onToggle={() => setExpandedActivityId(prev => prev === key ? null : key)}
                                                        onSaved={(updated) => {
                                                            // Optimistic local merge — tab feed + right rail
                                                            // share state so both re-render on save.
                                                            setTimeline(prev => prev.map(t => {
                                                                const tKey = t.activity_id || t.id;
                                                                return tKey === (updated.activity_id) ? { ...t, ...updated } : t;
                                                            }));
                                                            setActivities(prev => prev.map(a =>
                                                                a.activity_id === updated.activity_id ? { ...a, ...updated } : a
                                                            ));
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* ─── EVENT PHOTOS TAB ─── */}
                        {activeTab === 'event_photos' && (
                            <ContactEventPhotos contactId={contactId} />
                        )}

                        {/* ─── CHANGELOG TAB ─── */}
                        {activeTab === 'changelog' && (
                            <div className="p-4 bg-white rounded-xl border border-stone-200 sm:p-5" data-testid="changelog-section">
                                <h3 className="mb-4 text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Change Log</h3>
                                {changeLog.length === 0 ? (
                                    <div className="py-10 text-center text-stone-400">
                                        <History className="mx-auto mb-2 w-10 h-10 opacity-40" />
                                        <p className="text-sm">No changes recorded yet</p>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="absolute top-0 bottom-0 left-4 w-px bg-stone-200" />
                                        <div className="space-y-4">
                                            {changeLog.map((entry, idx) => (
                                                <div key={entry.log_id || idx} className="flex relative gap-3 pl-0 sm:gap-4" data-testid={`changelog-entry-${idx}`}>
                                                    <div className="flex z-10 flex-shrink-0 justify-center items-center w-8 h-8 rounded-full border-2 bg-stone-100 border-stone-300">
                                                        <Edit3 className="w-3.5 h-3.5 text-stone-600" />
                                                    </div>
                                                    <div className="flex-1 p-3 min-w-0 rounded-lg border bg-stone-50 border-stone-200 sm:p-4">
                                                        <div className="flex flex-col gap-1 justify-between mb-2 sm:flex-row sm:items-center">
                                                            <div className="flex gap-2 items-center">
                                                                <span className="text-sm font-medium text-stone-800">{entry.user_name || 'Unknown'}</span>
                                                                <span className="text-xs text-stone-400">{entry.changes?.length || 0} change{(entry.changes?.length || 0) !== 1 ? 's' : ''}</span>
                                                            </div>
                                                            <span className="flex gap-1 items-center text-xs text-stone-400">
                                                                <Clock className="w-3 h-3" />{formatDate(entry.timestamp)}
                                                            </span>
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            {(entry.changes || []).map((change, ci) => (
                                                                <div key={ci} className="flex flex-wrap gap-1 items-start text-xs sm:gap-2" data-testid={`change-${idx}-${ci}`}>
                                                                    <span className="font-semibold text-stone-600 min-w-[70px] sm:min-w-[90px]">{FIELD_LABELS[change.field] || change.field}</span>
                                                                    <span className="text-red-400 line-through max-w-[140px] sm:max-w-[180px] truncate">{change.old_value || '(empty)'}</span>
                                                                    <span className="text-stone-300">&rarr;</span>
                                                                    <span className="text-emerald-600 font-medium max-w-[140px] sm:max-w-[180px] truncate">{change.new_value || '(empty)'}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ─── AI CONTEXT TAB ─── */}
                        {activeTab === 'ai_context' && (
                            <div className="p-4 bg-white rounded-xl border border-stone-200 sm:p-5">
                                <MdFilePanel type="contact" entityId={contactId} />
                            </div>
                        )}

                        {/* ─── PITCHBOOK TAB ─── */}
                        {activeTab === 'pitchbook' && (
                            <PitchBookRecordTab crmType="contact" crmId={contactId} />
                        )}
                    </div>
                </div>

                {/* ─── RIGHT SIDEBAR ─── */}
                <div className="overflow-y-auto w-full bg-white border-t xl:w-72 xl:flex-shrink-0 xl:border-t-0 xl:border-l border-stone-200">
                    {/* Score Cards */}
                    <div className="p-4 border-b border-stone-100">
                        <div className="grid grid-cols-2 gap-2">
                            <div className="p-3 text-center rounded-lg bg-stone-100">
                                <p className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{contact.influence_score || 0}</p>
                                <p className="text-xs text-stone-600">Influence</p>
                            </div>
                            <div className="p-3 text-center bg-violet-50 rounded-lg">
                                <p className="text-lg font-bold text-violet-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{contact.engagement_score || 0}</p>
                                <p className="text-xs text-violet-500">Engagement</p>
                            </div>
                        </div>
                    </div>

                    {/* Associated Deals */}
                    <div className="p-4 border-b border-stone-100">
                        <h4 className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Deals ({deals.length})</h4>
                        {deals.length === 0 ? (
                            <div className="py-4 text-center">
                                <Briefcase className="mx-auto mb-1 w-8 h-8 text-stone-200" />
                                <p className="text-xs text-stone-400">No associated deals</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {deals.slice(0, 5).map(d => (
                                    <button key={d.deal_id} onClick={() => navigate(`/deals/${d.deal_id}`)} className="w-full text-left p-2.5 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-100/30 transition-colors" data-testid={`deal-${d.deal_id}`}>
                                        <p className="text-sm font-medium truncate text-stone-800">{d.name}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-xs capitalize text-stone-400">{d.stage}</span>
                                            {d.amount && <span className="text-xs font-medium text-emerald-600">${Number(d.amount).toLocaleString()}</span>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Recent Activities — rows open the same detail modal
                        as the Activities tab so note viewing / editing is
                        one-click from the rail. */}
                    <div className="p-4 border-b border-stone-100">
                        <h4 className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Recent Activity ({activities.length})</h4>
                        {activities.length === 0 ? (
                            <div className="py-4 text-center">
                                <Activity className="mx-auto mb-1 w-8 h-8 text-stone-200" />
                                <p className="text-xs text-stone-400">No activity</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {activities.slice(0, 4).map(a => (
                                    <button
                                        type="button"
                                        key={a.activity_id}
                                        onClick={() => {
                                            // Switch to the Activities tab and deep-link to
                                            // the clicked card so it auto-expands + scrolls.
                                            setActiveTab('activities');
                                            setExpandedActivityId(a.activity_id);
                                        }}
                                        data-action="activity.open_detail"
                                        className="w-full text-left flex items-start gap-2 py-1.5 px-2 rounded-md hover:bg-stone-100/60 transition-colors"
                                    >
                                        <div className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <FileText className="w-3 h-3 text-stone-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-medium truncate text-stone-700">{a.title}</p>
                                            <p className="text-xs capitalize text-stone-400">{a.type}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Campaigns */}
                    <div className="p-4">
                        <h4 className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Campaigns</h4>
                        <div className="py-4 text-center">
                            <Workflow className="mx-auto mb-1 w-8 h-8 text-stone-200" />
                            <p className="text-xs text-stone-400">Enroll in a campaign</p>
                            <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => setShowEnrollModal(true)} data-testid="sidebar-enroll-campaign-btn">
                                <Workflow className="mr-1 w-3 h-3" />Enroll Now
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enroll in Campaign Modal */}
            <EnrollCampaignModal
                isOpen={showEnrollModal}
                onClose={() => setShowEnrollModal(false)}
                contacts={contact ? [contact] : []}
                onSuccess={() => setShowEnrollModal(false)}
            />


            {/* DISC Editor Modal */}
            <DISCEditor
                open={showDISCEditor}
                onOpenChange={setShowDISCEditor}
                contactId={contact?.contact_id}
                contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()}
                currentScores={contact?.disc_scores}
                onSave={(disc) => setContact(prev => ({ ...prev, ...disc }))}
            />

            {/* New unified Record/Schedule modal */}
            <MeetingActionModal
                open={showMeetingModal}
                onOpenChange={setShowMeetingModal}
                contact={contact}
                account={account}
                deal={deals?.[0]}
                defaultTab={meetingModalTab}
                getAuthHeader={getAuthHeader}
                onDone={() => loadData()}
            />

            {/* Email composer (templates + signatures + AI writer with contact MD context) */}
            <ContactEmailComposer
                open={showEmailComposer}
                onOpenChange={setShowEmailComposer}
                contact={contact}
                account={account}
                deal={deals?.[0]}
                getAuthHeader={getAuthHeader}
                onSent={() => loadData()}
            />

            {/* Legacy Meeting Scheduler Dialog (kept as fallback, no longer opened from UI) */}
            <Dialog open={showMeetingScheduler} onOpenChange={setShowMeetingScheduler}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Schedule Meeting</DialogTitle>
                    </DialogHeader>
                    <div className="py-2 space-y-3">
                        <div>
                            <label className="block mb-1 text-xs font-medium text-stone-600">Title</label>
                            <input value={meetingForm.title}
                                onChange={e => setMeetingForm(p => ({ ...p, title: e.target.value }))}
                                placeholder="Meeting title..."
                                className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block mb-1 text-xs font-medium text-stone-600">Date</label>
                                <input type="date" value={meetingForm.date}
                                    onChange={e => setMeetingForm(p => ({ ...p, date: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                            <div>
                                <label className="block mb-1 text-xs font-medium text-stone-600">Duration</label>
                                <select value={`${meetingForm.startTime}-${meetingForm.endTime}`}
                                    onChange={e => {
                                        const [s, end] = e.target.value.split('-');
                                        setMeetingForm(p => ({ ...p, startTime: s, endTime: end }));
                                    }}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                    <option value="09:00-09:15">15 min</option>
                                    <option value="09:00-09:30">30 min</option>
                                    <option value="09:00-10:00">1 hour</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block mb-1 text-xs font-medium text-stone-600">Start Time</label>
                                <input type="time" value={meetingForm.startTime}
                                    onChange={e => setMeetingForm(p => ({ ...p, startTime: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                            <div>
                                <label className="block mb-1 text-xs font-medium text-stone-600">End Time</label>
                                <input type="time" value={meetingForm.endTime}
                                    onChange={e => setMeetingForm(p => ({ ...p, endTime: e.target.value }))}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            </div>
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-stone-600">Location</label>
                            <select value={meetingForm.location}
                                onChange={e => setMeetingForm(p => ({ ...p, location: e.target.value }))}
                                className="px-3 py-2 w-full text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                <option value="google_meet">Google Meet</option>
                                <option value="zoom">Zoom</option>
                                <option value="phone">Phone</option>
                                <option value="in_person">In Person</option>
                            </select>
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-stone-600">
                                Attendees {contact?.email && <span className="font-normal text-stone-400">({contact.email} auto-included)</span>}
                            </label>
                            <ContactEmailTypeahead
                                value={meetingForm.attendees}
                                onChange={atts => setMeetingForm(p => ({ ...p, attendees: atts }))}
                                placeholder="Add attendees..."
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs font-medium text-stone-600">Notes</label>
                            <div className="flex gap-1.5">
                                <textarea value={meetingForm.notes}
                                    onChange={e => setMeetingForm(p => ({ ...p, notes: e.target.value }))}
                                    rows={2} placeholder="Meeting agenda or notes..."
                                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                <VoiceButton onTranscript={t => setMeetingForm(p => ({ ...p, notes: p.notes ? `${p.notes} ${t}` : t }))} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowMeetingScheduler(false)}>Cancel</Button>
                        <Button onClick={handleScheduleMeeting}>Schedule</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Follow-Up Prompt */}
            <FollowUpPrompt
                open={!!followUpPrompt}
                onClose={() => setFollowUpPrompt(null)}
                contactId={contactId}
                contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : ''}
                accountId={contact?.account_id}
                interactionType={followUpPrompt?.type}
            />
        </div>
    );
}
